'use client'

// frontend/src/components/hr-auto/SystemBadge.tsx
//
// One system key in, one 22px tinted disc out. Plan 02 section 5.
//
// WHY A TINTED DISC AND NOT A BARE LOGO
// Two of the marks we actually hold are monochrome (Workday is a black glyph) and
// one is full bleed (the LinkedIn "in" fills its tile edge to edge). A bare 16px
// image cannot do the grey to colour transition for the monochrome ones, and that
// transition is, per plan 02 section 5, "a large part of why the fan-out reads as
// real". So the disc carries the colour and the mark sits inside it: `inset` marks
// float on the tint, `bleed` marks fill the disc.
//
// WHAT WE HOLD AND WHAT WE LETTERMARK
// Reused from `frontend/public/logos/`: workday.svg, outlook.svg, sap.svg and the
// square LinkedIn mark. Lettermarked, because the asset we hold would be illegible
// or we hold none: ServiceNow (the only asset is a 3840x570 wordmark, which at 22px
// is a smudge), Civil Service Jobs, DBS and Active Directory. Plan 02 section 5 is
// explicit that a lettermark tile beats a wrong or stretched logo.

import Image from 'next/image'
import { Icons } from '@/components/ui/icons'
import { cn } from '@/lib/utils'
import type { SystemKey } from './types'

type MarkFit = 'inset' | 'bleed'

interface SystemMeta {
  label: string
  /** The brand colour. Tints the disc, and fills the letters on a lettermark. */
  color: string
  logo?: string
  fit?: MarkFit
  letters?: string
  icon?: React.ElementType
}

export const SYSTEM_META: Record<SystemKey, SystemMeta> = {
  internal: {
    label: 'Corporate services knowledge base',
    color: '#141A42',
    icon: Icons.brain,
  },
  policies: {
    label: 'AI Policies',
    color: '#535EA4',
    icon: Icons.shield,
  },
  workday: {
    label: 'Workday',
    color: '#F38B00',
    logo: '/logos/workday.svg',
    fit: 'inset',
  },
  civil_service_jobs: {
    label: 'Civil Service Jobs',
    color: '#1D70B8',
    letters: 'CS',
  },
  linkedin: {
    label: 'LinkedIn',
    color: '#0A66C2',
    logo: '/logos/linkedin_talent_solutions_logo.jpeg',
    fit: 'bleed',
  },
  outlook: {
    label: 'Outlook',
    color: '#0364B8',
    logo: '/logos/outlook.svg',
    fit: 'inset',
  },
  servicenow: {
    label: 'ServiceNow',
    color: '#0B7A5F',
    letters: 'SN',
  },
  sap_payroll: {
    label: 'SAP Payroll',
    color: '#0FAAFF',
    logo: '/logos/sap.svg',
    fit: 'bleed',
  },
  active_directory: {
    label: 'Active Directory',
    color: '#475569',
    icon: Icons.key,
  },
  dbs: {
    label: 'DBS checking service',
    color: '#1D70B8',
    icon: Icons.shield,
  },
}

export function systemLabel(system: SystemKey): string {
  return SYSTEM_META[system]?.label ?? 'Corporate services knowledge base'
}

interface SystemBadgeProps {
  system: SystemKey
  /** A dormant badge is grey. It lights to full colour when the step starts. */
  active: boolean
  /** 22px in a step row, 28px in the connected-systems strip on the empty state. */
  size?: number
  className?: string
}

export function SystemBadge({ system, active, size = 22, className }: SystemBadgeProps) {
  const meta = SYSTEM_META[system] ?? SYSTEM_META.internal
  const Icon = meta.icon
  const inner = Math.round(size * 0.58)

  return (
    <span
      aria-hidden='true'
      className={cn(
        'relative inline-grid shrink-0 place-items-center overflow-hidden rounded-full',
        'transition-all duration-500 ease-out',
        className
      )}
      style={{
        width: size,
        height: size,
        backgroundColor: active ? `${meta.color}1F` : 'rgba(20, 26, 66, 0.05)',
        boxShadow: active ? `inset 0 0 0 1px ${meta.color}33` : 'inset 0 0 0 1px rgba(20,26,66,0.06)',
      }}
    >
      {meta.logo ? (
        <Image
          src={meta.logo}
          alt=''
          width={size}
          height={size}
          unoptimized
          className={cn(
            'transition-all duration-500 ease-out',
            meta.fit === 'bleed' ? 'h-full w-full object-cover' : 'object-contain',
            active ? 'opacity-100 grayscale-0' : 'opacity-40 grayscale'
          )}
          style={meta.fit === 'bleed' ? undefined : { width: inner, height: inner }}
        />
      ) : meta.letters ? (
        <span
          className='font-display font-semibold leading-none transition-colors duration-500'
          style={{
            fontSize: Math.round(size * 0.42),
            color: active ? meta.color : 'rgba(132, 142, 170, 0.75)',
          }}
        >
          {meta.letters}
        </span>
      ) : Icon ? (
        <Icon
          strokeWidth={1.7}
          className='transition-colors duration-500'
          style={{
            width: inner,
            height: inner,
            color: active ? meta.color : 'rgba(132, 142, 170, 0.75)',
          }}
        />
      ) : null}
    </span>
  )
}

/**
 * The connected-systems strip on the empty state.
 *
 * It is the first thing the room reads, and it is the claim the step player then
 * makes good on, so the two are drawn from the same table by construction.
 */
export function SystemStrip({ systems }: { systems: SystemKey[] }) {
  return (
    <div className='flex flex-wrap items-center justify-center gap-x-4 gap-y-2'>
      {systems.map((key) => (
        <span key={key} className='flex items-center gap-2'>
          <SystemBadge system={key} active size={24} />
          <span className='text-[13px] text-brand-muted'>{systemLabel(key)}</span>
        </span>
      ))}
    </div>
  )
}
